import { getBrowseById } from "#skyward_browseList";
import { getLocalStorage } from "./skyward.base.volatileStorage";
import { Browse } from "./types";

export type BrowseIdentifier = string | number | Browse;

interface BrowsePreferences
{
    quickFilterOpen?: boolean | null;
    seekId: number | null;
    prevSeekId: number | null;
    nextSeekId: number | null;
}

const skyward = window.skyward;

const volatileStorage = getLocalStorage("BrowsePreference");
const pageStorage: Record<string, BrowsePreferences> = Object.create(null);

const defaultBrowsePreferences: BrowsePreferences =
{
    quickFilterOpen: null,
    seekId: null,
    prevSeekId: null,
    nextSeekId: null
};

export function getPreferences(browse: BrowseIdentifier)
{
    const key = lookupBrowseKey(browse);
    if (!key)
    {
        return Object.assign({}, defaultBrowsePreferences) as BrowsePreferences;
    }

    return get(String(key));
}

export function setPreferences(browse: BrowseIdentifier, preferences: Partial<BrowsePreferences>)
{
    const numericKey = lookupBrowseKey(browse);
    if (!numericKey)
    {
        return;
    }
    const key = String(numericKey);

    const definedPreferences = getDefinedPreferences(preferences);
    if (Object.keys(definedPreferences).length === 0)
    {
        return;
    }

    const browsePreferences = Object.assign(get(key), definedPreferences);

    if (browsePreferencesAreEqual(browsePreferences, defaultBrowsePreferences))
    {
        remove(key);
        return;
    }

    set(key, browsePreferences);
}

function lookupBrowseKey(browse: BrowseIdentifier)
{
    if (typeof browse === "string")
    {
        const browseDef = getBrowseById(browse);
        if (!browseDef?.browseId)
        {
            console.warn("Browse Not Found in BrowseList", browse);
            return;
        }
        return getKey(browseDef.browseId as unknown as number);
    }
    else if (typeof browse === "object" && browse.browseId)
    {
        return getKey(browse.browseId as unknown as number);
    }
    else if (typeof browse === "number")
    {
        return getKey(browse);
    }

    console.warn("Browse Not Found in BrowseList", browse);
    return;
}

function get(key: string)
{
    const storedBrowsePreferences = volatileStorage.get(key) || pageStorage[key];

    return Object.assign({}, defaultBrowsePreferences, storedBrowsePreferences) as BrowsePreferences;
}

function remove(key: string)
{
    volatileStorage.remove(key);
    delete pageStorage[key];
}

function set(key: string, preferences: BrowsePreferences)
{
    volatileStorage.set(key, preferences);
    pageStorage[key] = preferences;
}

function getKey(browseId: number)
{
    const key = [skyward.global.userIdHash, skyward.global.windowGuid, browseId].join(",");
    return hash(key);
}

function getDefinedPreferences(preferences: Partial<BrowsePreferences>): Partial<BrowsePreferences>
{
    const definedPreferences: Partial<BrowsePreferences> = {};

    if (preferences.quickFilterOpen !== undefined)
    {
        definedPreferences.quickFilterOpen = preferences.quickFilterOpen;
    }

    if (preferences.seekId !== undefined)
    {
        definedPreferences.seekId = preferences.seekId;
    }

    if (preferences.prevSeekId !== undefined)
    {
        definedPreferences.prevSeekId = preferences.prevSeekId;
    }

    if (preferences.nextSeekId !== undefined)
    {
        definedPreferences.nextSeekId = preferences.nextSeekId;
    }

    return definedPreferences;
}

function hash(str: string)
{
    // A simple hashing algorithm that emulates Java's String hash (https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript)

    let hash = 0;

    for (let i = 0; i < str.length; i++)
    {
        const chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }

    return hash;
}

function browsePreferencesAreEqual(preference1: BrowsePreferences, preference2: BrowsePreferences)
{
    // TODO (5772911) consider replacing with skyward.global.deepEqual
    if (preference1 === null || preference2 === null)
    {
        return preference1 === null && preference2 === null;
    }

    const preference1Keys = Object.keys(preference1);
    const preference2Keys = Object.keys(preference2);

    if (preference1Keys.length !== preference2Keys.length)
    {
        return false;
    }

    preference1Keys.sort();
    preference2Keys.sort();

    for (let i = 0; i < preference1Keys.length; i++)
    {
        const prop = preference1Keys[i];

        if (prop !== preference2Keys[i])
        {
            return false;
        }

        if ((preference1 as any)[prop] !== (preference2 as any)[prop])
        {
            return false;
        }
    }

    return true;
}

